import { ChevronDown, ChevronUp } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { getApiKey } from 'utils/funcs/browser/getApiKey'
import { generateEncryptedKey } from 'utils/funcs/byot/generateEncryptedKey'
import AccountSid from '../../../../Parameters/AccountSid'
import AuthToken from '../../../../Parameters/AuthToken'
import 'react-toastify/dist/ReactToastify.css'

function GenerateKey() {
  const [encryptedKey, setEncryptedKey] = useState('')
  const [s3ExternalId, setS3ExternalId] = useState('')
  const [blandAccountId, setBlandAccountId] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [useS3Storage, setUseS3Storage] = useState(false)
  const apiKey = getApiKey()
  const methods = useForm()

  // Generate external ID mutation
  const generateExternalIdMutation = useMutation({
    mutationFn: async (encryptedKey) => {
      const { data } = await $fetch('/byot/s3_config/generate_external_id', {
        method: 'POST',
        headers: {
          encrypted_key: encryptedKey,
        },
      })
      return data
    },
    onSuccess: (data) => {
      setS3ExternalId(data.s3_external_id)
      setBlandAccountId(data.bland_account_id)
    },
    onError: (error) => {
      console.error('Failed to generate external ID:', error)
      toast.error('Failed to generate external ID')
    },
  })

  // Save S3 role ARN mutation
  const saveS3RoleArnMutation = useMutation({
    mutationFn: async ({ encryptedKey, roleArn }) => {
      await $fetch('/byot/s3_config', {
        method: 'POST',
        headers: {
          encrypted_key: encryptedKey,
        },
        body: {
          s3_role_arn: roleArn,
        },
      })
    },
    onError: (error) => {
      console.error('Failed to save S3 role ARN:', error)
    },
  })

  // Generate encrypted key mutation
  const generateKeyMutation = useMutation({
    mutationFn: async (formData) => {
      const { account_sid, auth_token, s3_role_arn } = formData
      const response = await generateEncryptedKey(
        apiKey,
        account_sid,
        auth_token,
        useS3Storage ? s3_role_arn : null,
      )
      if (response.status !== 'success') {
        throw new Error('Failed to generate key')
      }
      return { response, s3_role_arn }
    },
    onSuccess: async ({ response, s3_role_arn }) => {
      toast.success('Encrypted key generated successfully!')
      setEncryptedKey(response.encrypted_key)
      
      // If S3 is enabled, handle the external ID generation
      if (useS3Storage && response.encrypted_key) {
        // First, generate external ID if we don't have one
        if (!s3ExternalId) {
          await generateExternalIdMutation.mutateAsync(response.encrypted_key)
        }
        
        // Then save the role ARN if provided
        if (s3_role_arn) {
          await saveS3RoleArnMutation.mutateAsync({ 
            encryptedKey: response.encrypted_key, 
            roleArn: s3_role_arn 
          })
        }
      }
    },
    onError: (error) => {
      console.error(error)
      toast.error('An error occurred while generating the key')
    },
  })

  const onSubmit = (formData) => {
    generateKeyMutation.mutate(formData)
  }

  useEffect(() => {
    methods.setValue('encrypted_key', encryptedKey)
  }, [encryptedKey, methods])

  // Handle S3 checkbox change
  const handleS3CheckboxChange = (checked) => {
    setUseS3Storage(checked)
    // If we already have an encrypted key, generate external ID immediately
    if (checked && encryptedKey && !s3ExternalId) {
      generateExternalIdMutation.mutate(encryptedKey)
    }
  }

  return (
    <div className="mx-auto max-w-2xl rounded-md bg-white p-4 shadow-md">
      <h2 className="mb-4 text-lg font-semibold">Generate Encrypted Key</h2>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-y-4"
        >
          {!encryptedKey
            ? (
                <>
                  <p className="mb-2.5 text-gray-600">
                    Enter your Twilio "Account SID" and "Auth Token." We'll generate
                    an Encrypted Key that you can use in your API requests.
                  </p>
                  <div className="flex flex-col gap-y-2.5">
                    <AccountSid fieldName="account_sid" />
                    <div className="text-2xs text-gray-500">
                      Hint: Account SID typically starts with "AC" and is 34
                      characters long.
                    </div>
                    <AuthToken fieldName="auth_token" />
                    <div className="text-2xs text-gray-500">
                      Hint: Auth Token is typically 32 characters long.
                    </div>
                  </div>

                  {/* Advanced Settings Section */}
                  <div className="mt-4 border-t pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className={`
                        flex w-full items-center justify-between text-left
                      `}
                    >
                      <span className="text-xs font-medium text-gray-700">
                        Advanced Settings
                      </span>
                      {showAdvanced
                        ? (
                            <ChevronUp className="h-3 w-3 text-gray-400" />
                          )
                        : (
                            <ChevronDown className="h-3 w-3 text-gray-400" />
                          )}
                    </button>

                    {showAdvanced && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={useS3Storage}
                              onChange={e => handleS3CheckboxChange(e.target.checked)}
                              className={`
                                mr-2 h-3 w-3 rounded border-gray-300
                                text-indigo-600
                                focus:ring-indigo-500
                              `}
                            />
                            <span className="text-xs text-gray-700">
                              Use S3 for recording storage
                            </span>
                          </label>
                          <p className="mt-1 text-2xs text-gray-500">
                            Enable this if your Twilio account stores recordings in an S3 bucket
                          </p>
                        </div>

                        {useS3Storage && (
                          <div className="space-y-3">
                            {!encryptedKey ? (
                              <div className="space-y-3">
                                <div className="rounded-md bg-yellow-50 p-2">
                                  <p className="text-xs text-yellow-700">
                                    S3 configuration will be set up after generating your encrypted key.
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700">
                                    Bland Account ID
                                  </label>
                                  <input
                                    type="text"
                                    value="344997732581"
                                    readOnly
                                    className={`
                                      mt-0.5 block w-full rounded border border-gray-300 bg-gray-50
                                      px-2 py-1.5 shadow-xs sm:text-xs
                                    `}
                                  />
                                  <p className="mt-1 text-2xs text-gray-500">
                                    You'll use this Account ID when creating your AWS IAM role
                                  </p>
                                </div>
                                
                                <div className="rounded-md bg-blue-50 p-2">
                                  <p className="text-xs text-blue-600">
                                    <strong>Note:</strong> After generating your encrypted key, you'll receive an External ID to use in your IAM role's trust policy.
                                  </p>
                                </div>
                              </div>
                            ) : generateExternalIdMutation.isPending
                              ? (
                                  <p className="text-xs text-gray-500">Generating S3 configuration...</p>
                                )
                              : s3ExternalId && blandAccountId
                                ? (
                                    <>
                                      <div>
                                        <label className={`
                                          block text-xs font-medium
                                          text-gray-700
                                        `}
                                        >
                                          External ID
                                        </label>
                                        <input
                                          type="text"
                                          value={s3ExternalId}
                                          readOnly
                                          className={`
                                            mt-0.5 block w-full rounded border
                                            border-gray-300 bg-gray-50 px-2
                                            py-1.5 shadow-xs
                                            sm:text-xs
                                          `}
                                        />
                                        <p className={`
                                          mt-1 text-2xs text-gray-500
                                        `}
                                        >
                                          Use this External ID in your AWS IAM role trust policy
                                        </p>
                                      </div>

                                      <div>
                                        <label className={`
                                          block text-xs font-medium
                                          text-gray-700
                                        `}
                                        >
                                          Bland Account ID
                                        </label>
                                        <input
                                          type="text"
                                          value={blandAccountId}
                                          readOnly
                                          className={`
                                            mt-0.5 block w-full rounded border
                                            border-gray-300 bg-gray-50 px-2
                                            py-1.5 shadow-xs
                                            sm:text-xs
                                          `}
                                        />
                                        <p className={`
                                          mt-1 text-2xs text-gray-500
                                        `}
                                        >
                                          Use this Account ID in your AWS IAM role trust policy
                                        </p>
                                      </div>

                                      <div className="rounded-md bg-blue-50 p-3">
                                        <h4 className={`
                                          mb-2 text-xs font-semibold
                                          text-blue-700
                                        `}
                                        >
                                          Setup Instructions:
                                        </h4>
                                        <ol className={`
                                          list-inside list-decimal space-y-1
                                          text-2xs text-blue-600
                                        `}
                                        >
                                          <li>Create an IAM role in your AWS account</li>
                                          <li>Use the Account ID and External ID shown above in the trust policy</li>
                                          <li>Grant the role read access to your S3 bucket</li>
                                          <li>After creating the role, enter its ARN below</li>
                                        </ol>
                                      </div>

                                      <div>
                                        <label
                                          htmlFor="s3_role_arn"
                                          className={`
                                            block text-xs font-medium
                                            text-gray-700
                                          `}
                                        >
                                          Your AWS Role ARN
                                        </label>
                                        <input
                                          type="text"
                                          id="s3_role_arn"
                                          {...methods.register('s3_role_arn', {
                                            required: useS3Storage,
                                            pattern: {
                                              value: /^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/,
                                              message: 'Invalid Role ARN format',
                                            },
                                          })}
                                          placeholder="arn:aws:iam::123456789012:role/YourRoleName"
                                          className={`
                                            mt-0.5 block w-full rounded border
                                            border-gray-300 px-2 py-1.5
                                            shadow-xs
                                            focus:border-indigo-500
                                            focus:ring-indigo-500
                                            focus:outline-hidden
                                            sm:text-xs
                                          `}
                                        />
                                        {methods.formState.errors.s3_role_arn && (
                                          <p className={`
                                            mt-1 text-2xs text-red-600
                                          `}
                                          >
                                            {methods.formState.errors.s3_role_arn.message}
                                          </p>
                                        )}
                                      </div>
                                    </>
                                  )
                                : null}
                            
                            {/* Always show the Role ARN field when S3 is enabled */}
                            {(!s3ExternalId || !encryptedKey) && (
                              <div>
                                <label
                                  htmlFor="s3_role_arn"
                                  className={`
                                    block text-xs font-medium
                                    text-gray-700
                                  `}
                                >
                                  Your AWS Role ARN (Optional)
                                </label>
                                <input
                                  type="text"
                                  id="s3_role_arn"
                                  {...methods.register('s3_role_arn', {
                                    required: false,
                                    pattern: {
                                      value: /^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/,
                                      message: 'Invalid Role ARN format',
                                    },
                                  })}
                                  placeholder="arn:aws:iam::123456789012:role/YourRoleName"
                                  className={`
                                    mt-0.5 block w-full rounded border
                                    border-gray-300 px-2 py-1.5
                                    shadow-xs
                                    focus:border-indigo-500
                                    focus:ring-indigo-500
                                    focus:outline-hidden
                                    sm:text-xs
                                  `}
                                />
                                {methods.formState.errors.s3_role_arn && (
                                  <p className={`
                                    mt-1 text-2xs text-red-600
                                  `}
                                  >
                                    {methods.formState.errors.s3_role_arn.message}
                                  </p>
                                )}
                                <p className="mt-1 text-2xs text-gray-500">
                                  You can enter this now or add it later in the Numbers tab
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={`
                    mb-2.5 border-l-4 border-blue-400 bg-blue-50 p-2.5
                  `}
                  >
                    <p className="text-xs text-blue-700">
                      The Encrypted Key will only be displayed to you for security
                      and cannot be retrieved later without regenerating.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={generateKeyMutation.isPending}
                    className={`
                      w-full rounded border border-transparent bg-indigo-600
                      px-2.5 py-1.5 text-xs font-medium text-white shadow-xs
                      hover:bg-indigo-700
                      focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                      focus:outline-hidden
                      ${
                generateKeyMutation.isPending ? 'cursor-not-allowed opacity-50' : ''
                }
                    `}
                  >
                    {generateKeyMutation.isPending ? 'Generating...' : 'Generate Key'}
                  </button>
                </>
              )
            : (
                <>
                  <div>
                    <label
                      htmlFor="encrypted_key"
                      className="block text-xs font-medium text-gray-700"
                    >
                      Encrypted Key
                    </label>
                    <input
                      type="text"
                      id="encrypted_key"
                      {...methods.register('encrypted_key')}
                      readOnly
                      className={`
                        mt-0.5 block w-full rounded border border-gray-300 px-2
                        py-1.5 shadow-xs
                        focus:border-indigo-500 focus:ring-indigo-500
                        focus:outline-hidden
                        sm:text-xs
                      `}
                    />
                  </div>
                  <div className={`
                    border-l-4 border-yellow-400 bg-yellow-50 p-2.5
                  `}
                  >
                    <p className="text-xs text-yellow-700">
                      Save this! The Encrypted Key will only be displayed to you for
                      security and cannot be retrieved later without regenerating.
                    </p>
                  </div>

                  {useS3Storage && s3ExternalId && (
                    <div className="mt-4">
                      <h3 className="mb-2 text-xs font-semibold text-gray-700">
                        S3 Configuration Details
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className={`
                            block text-xs font-medium text-gray-700
                          `}
                          >
                            External ID
                          </label>
                          <input
                            type="text"
                            value={s3ExternalId}
                            readOnly
                            className={`
                              mt-0.5 block w-full rounded border border-gray-300
                              bg-gray-50 px-2 py-1.5 shadow-xs
                              sm:text-xs
                            `}
                          />
                          <p className="mt-1 text-2xs text-gray-500">
                            Use this External ID in your AWS IAM role trust policy
                          </p>
                        </div>

                        <div>
                          <label className={`
                            block text-xs font-medium text-gray-700
                          `}
                          >
                            Bland Account ID
                          </label>
                          <input
                            type="text"
                            value={blandAccountId || '344997732581'}
                            readOnly
                            className={`
                              mt-0.5 block w-full rounded border border-gray-300
                              bg-gray-50 px-2 py-1.5 shadow-xs
                              sm:text-xs
                            `}
                          />
                          <p className="mt-1 text-2xs text-gray-500">
                            Use this Account ID in your AWS IAM role trust policy
                          </p>
                        </div>

                        <div className="rounded-md bg-gray-50 p-3">
                          <h4 className="mb-1 text-xs font-medium text-gray-700">
                            Next Steps:
                          </h4>
                          <ol className={`
                            list-inside list-decimal space-y-1 text-2xs
                            text-gray-600
                          `}
                          >
                            <li>Navigate to the Numbers tab to complete S3 configuration</li>
                            <li>Enter the ARN of the IAM role you created</li>
                            <li>Save your S3 configuration</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
        </form>
      </FormProvider>
    </div>
  )
}

export default GenerateKey
